import React, { useState, useEffect, useMemo } from "react";
import DSDialog from "../../system-design/DSDialog";
import DSDialogTitle from "../../system-design/DSDialogTitle";
import DSDialogContent from "../../system-design/DSDialogContent";
import DSDialogActions from "../../system-design/DSDialogActions";
import DSAlert from "../../system-design/DSAlert";
import DSTextField from "../../system-design/DSTextField";
import DSPrimaryButton from "../../system-design/DSPrimaryButton";
import DSSecondaryButton from "../../system-design/DSSecondaryButton";
import DSCircularProgress from "../../system-design/DSCircularProgress";
import DSBox from "../../system-design/DSBox";
import DSButton from "../../system-design/DSButton";

enum LabelCreationMode {
  SINGLE = "single",
  BULK = "bulk",
}

interface CreateCollectionLabelModalProps {
  show: boolean;
  onClose: () => void;
  createMutation: {
    mutateAsync: (data: { text: string }) => Promise<unknown>;
    isPending: boolean;
  };
  createBulkMutation: {
    mutateAsync: (data: { texts: string[] }) => Promise<{ length: number }>;
    isPending: boolean;
  };
  title?: string;
}

function CreateCollectionLabelModal({
  show,
  onClose,
  createMutation,
  createBulkMutation,
  title = "Create Tag(s)",
}: CreateCollectionLabelModalProps) {
  const [mode, setMode] = useState<LabelCreationMode>(LabelCreationMode.SINGLE);
  const [labelText, setLabelText] = useState("");
  const [bulkText, setBulkText] = useState("");

  const [errorMessage, setErrorMessage] = useState("");
  const [createdLabel, setCreatedLabel] = useState<string | null>(null);
  const [createdCount, setCreatedCount] = useState<number | null>(null);

  const { mutateAsync: createLabel, isPending: isCreatingSingle } =
    createMutation;
  const { mutateAsync: createLabelsBulk, isPending: isCreatingBulk } =
    createBulkMutation;

  const isCreating = isCreatingSingle || isCreatingBulk;

  const bulkLabels = useMemo(() => {
    return bulkText
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  }, [bulkText]);

  useEffect(() => {
    if (!show) {
      setMode(LabelCreationMode.SINGLE);
      setLabelText("");
      setBulkText("");
      setErrorMessage("");
      setCreatedLabel(null);
      setCreatedCount(null);
    }
  }, [show]);

  const handleCreate = async () => {
    setErrorMessage("");

    try {
      if (mode === LabelCreationMode.SINGLE) {
        await createLabel({ text: labelText });
        setCreatedLabel(labelText);
      } else {
        const result = await createLabelsBulk({ texts: bulkLabels });
        setCreatedCount(result.length);
      }

      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.log("Error:", error);

      let message = "Failed to create tag(s). Please try again.";

      if (typeof error === "object" && error !== null && "response" in error) {
        const axiosError = error as {
          response?: {
            status?: number;
            data?: {
              message?: string;
              error?: string;
            };
          };
        };

        if (axiosError.response?.data?.message) {
          message = axiosError.response.data.message;
        }
      }

      setErrorMessage(message);
    }
  };

  const isFormValid = () => {
    if (mode === LabelCreationMode.SINGLE) {
      return labelText.trim().length > 0;
    }
    return bulkLabels.length > 0;
  };

  return (
    <DSDialog open={show} onClose={onClose} maxWidth="sm" fullWidth>
      <DSDialogTitle>{title}</DSDialogTitle>
      <DSDialogContent>
        {errorMessage && (
          <DSAlert
            severity="error"
            onClose={() => setErrorMessage("")}
            sx={{ marginBottom: 2 }}
          >
            {errorMessage}
          </DSAlert>
        )}
        {createdLabel && (
          <DSAlert severity="success" sx={{ marginBottom: 2 }}>
            Tag created successfully: <strong>{createdLabel}</strong>
          </DSAlert>
        )}
        {createdCount !== null && (
          <DSAlert severity="success" sx={{ marginBottom: 2 }}>
            <strong>{createdCount}</strong> tag(s) created successfully!
          </DSAlert>
        )}

        <DSBox sx={{ display: "flex", gap: 1, marginBottom: 2 }}>
          <DSButton
            variant={
              mode === LabelCreationMode.SINGLE ? "contained" : "outlined"
            }
            onClick={() => setMode(LabelCreationMode.SINGLE)}
            disabled={isCreating || !!createdLabel || createdCount !== null}
          >
            Single
          </DSButton>
          <DSButton
            variant={mode === LabelCreationMode.BULK ? "contained" : "outlined"}
            onClick={() => setMode(LabelCreationMode.BULK)}
            disabled={isCreating || !!createdLabel || createdCount !== null}
          >
            Bulk
          </DSButton>
        </DSBox>

        {mode === LabelCreationMode.SINGLE ? (
          <DSTextField
            fullWidth
            label="Tag Text"
            placeholder="Enter Tag Text"
            value={labelText}
            onChange={(e) => setLabelText(e.target.value)}
            disabled={isCreating || !!createdLabel}
          />
        ) : (
          <DSBox>
            <DSTextField
              fullWidth
              label="Tags (one per line)"
              placeholder="Enter tags, one per line..."
              multiline
              rows={8}
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
              disabled={isCreating || createdCount !== null}
            />
            {bulkLabels.length > 0 && (
              <DSBox sx={{ color: "text.secondary", fontSize: 12, mt: 0.5 }}>
                {bulkLabels.length} tag(s) will be created
              </DSBox>
            )}
          </DSBox>
        )}
      </DSDialogContent>
      <DSDialogActions>
        <DSSecondaryButton onClick={onClose}>Close</DSSecondaryButton>
        <DSPrimaryButton
          onClick={handleCreate}
          disabled={
            isCreating ||
            !!createdLabel ||
            createdCount !== null ||
            !isFormValid()
          }
        >
          {isCreating ? (
            <>
              <DSCircularProgress size={16} sx={{ marginRight: 1 }} />{" "}
              Creating...
            </>
          ) : mode === LabelCreationMode.SINGLE ? (
            "Create Tag"
          ) : (
            `Create ${bulkLabels.length} Tag(s)`
          )}
        </DSPrimaryButton>
      </DSDialogActions>
    </DSDialog>
  );
}

export default CreateCollectionLabelModal;
